import useAuth from '@app/auth/use-auth';
import Page from '@app/components/layout/page/page';
import { useGetSecrResults } from '@shared/api/hooks/ccf/secr';
import Heading from '@shared/components/content/heading';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import ReactMarkdown from 'react-markdown';
import { generatePath, useNavigate } from 'react-router-dom';
import { ccfRoutes } from '../../ccf-routes';
import { useCCF } from '../ccf-context';
import { SECRReportCombinedTable } from './components/secr-report-combined-table';
import { SECRReportHero } from './components/secr-report-hero';
import { SECRReportIntensityTable } from './components/secr-report-intensity-table';
import { SecrReportLayoutHeader } from './components/secr-report-layout-header';
import { SECRReportResultsTable } from './components/secr-report-results-table';
import { SECRReportSection } from './components/secr-report-section';
import {
  buildFullReportHtml,
  buildFullReportText,
  buildMethodologyText,
  buildSections,
  copyHtmlAndText,
  deriveYearLabels,
  formatPeriod,
  type SectionContent,
} from './secr-report-utils';

const NOOP = () => {};

const ICON_CLASS = 'size-5 text-orange-400';

const SECTION_ICONS = {
  energy: <Icons.zap className={ICON_CLASS} />,
  emissions: <Icons.barChart className={ICON_CLASS} />,
  intensity: <Icons.barChart2 className={ICON_CLASS} />,
  methodology: <Icons.fileChartColumn className={ICON_CLASS} />,
  actions: <Icons.check className={ICON_CLASS} />,
};

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Page name="SECR report" page="guide">
      <div className="[--header-height:72px]">{children}</div>
    </Page>
  );
}

export default function SecrReportPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { uuid: reportUuid, startDate, endDate } = useCCF();
  const { user } = useAuth();

  const {
    data: result,
    isLoading: resultsLoading,
    isError: resultsError,
  } = useGetSecrResults(reportUuid);

  const handleBack = () =>
    navigate(
      generatePath(ccfRoutes.SECR_CONFIGURATION, { report_uuid: reportUuid })
    );

  if (resultsLoading) {
    return (
      <PageShell>
        <SecrReportLayoutHeader
          reportUuid={reportUuid}
          onBack={handleBack}
          onCopyAll={NOOP}
          onDownload={NOOP}
        />
        <main className="bg-muted/40 h-[calc(100vh-var(--header-height))] overflow-y-auto p-8">
          <div className="mx-auto max-w-4xl">
            <div className="space-y-10 rounded-sm bg-gray-00 px-12 py-10 shadow-xl">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-72 w-full" />
              <Skeleton className="h-72 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </main>
      </PageShell>
    );
  }

  if (resultsError || !result) {
    return (
      <PageShell>
        <SecrReportLayoutHeader
          reportUuid={reportUuid}
          onBack={handleBack}
          onCopyAll={NOOP}
          onDownload={NOOP}
        />
        <main className="bg-muted/40 h-[calc(100vh-var(--header-height))] overflow-y-auto p-8">
          <div className="mx-auto max-w-4xl">
            <div className="space-y-3 rounded-sm bg-gray-00 p-12 text-center shadow-xl">
              <Heading variant="section">Could not load SECR report</Heading>
              <Text variant="subtle">
                The report data is not available. The report may not yet be
                published, or there was a problem fetching it.
              </Text>
              <Button variant="outline" onClick={handleBack}>
                Back to configuration
              </Button>
            </div>
          </div>
        </main>
      </PageShell>
    );
  }

  const hasPriorYear = result.totals.priorYearTonnes != null;
  const { prevYearLabel, currentYearLabel } = deriveYearLabels(startDate);
  const period = formatPeriod(startDate, endDate);
  const organisationName = user?.organizationName ?? 'Organisation';

  const sections = buildSections({
    result,
    config: result.config,
    organisationName,
    period,
    prevYearLabel,
    currentYearLabel,
    hasPriorYear,
  });

  const handleCopySection = (section: SectionContent) => async () => {
    try {
      await copyHtmlAndText(section.html, section.text);
    } catch (error) {
      sentry.log(error);
      toast({ title: 'Failed to copy', variant: 'destructive' });
      throw error;
    }
  };

  const handleCopyAll = async () => {
    try {
      await copyHtmlAndText(
        buildFullReportHtml({ organisationName, period, sections }),
        buildFullReportText({ organisationName, period, sections })
      );
    } catch (error) {
      sentry.log(error);
      toast({ title: 'Failed to copy report', variant: 'destructive' });
      throw error;
    }
  };

  const handleDownload = () => {
    const path = generatePath(ccfRoutes.SECR_REPORT_DOWNLOAD, {
      report_uuid: reportUuid,
    });
    window.open(path, '_blank');
  };

  const methodologyParagraphs = (
    result.config.methodology?.trim() ||
    buildMethodologyText({
      organisationName,
      period,
      hasPriorYear,
      intensityLabel: result.intensity?.label,
      intensityUnit: result.intensity?.unit,
    })
  ).split('\n\n');

  return (
    <PageShell>
      <SecrReportLayoutHeader
        reportUuid={reportUuid}
        onBack={handleBack}
        onCopyAll={handleCopyAll}
        onDownload={handleDownload}
      />
      <main className="bg-muted/40 h-[calc(100vh-var(--header-height))] overflow-y-auto p-8">
        <div className="mx-auto max-w-4xl">
          <div className="space-y-10 rounded-sm bg-gray-00 px-12 py-10 shadow-xl">
            <SECRReportHero
              organisationName={organisationName}
              period={period}
            />

            {sections.combined ? (
              <SECRReportSection
                title={sections.combined.title}
                icon={SECTION_ICONS.emissions}
                onCopy={handleCopySection(sections.combined!)}
              >
                <SECRReportCombinedTable
                  categories={result.categories}
                  totals={result.totals}
                  currentYearLabel={currentYearLabel}
                />
              </SECRReportSection>
            ) : (
              <>
                {sections.energy && (
                  <SECRReportSection
                    title={sections.energy.title}
                    icon={SECTION_ICONS.energy}
                    onCopy={handleCopySection(sections.energy!)}
                  >
                    <SECRReportResultsTable
                      metric="energy"
                      categories={result.categories}
                      totals={result.totals}
                      hasPriorYear={hasPriorYear}
                      prevYearLabel={prevYearLabel}
                      currentYearLabel={currentYearLabel}
                    />
                  </SECRReportSection>
                )}
                {sections.emissions && (
                  <SECRReportSection
                    title={sections.emissions.title}
                    icon={SECTION_ICONS.emissions}
                    onCopy={handleCopySection(sections.emissions!)}
                  >
                    <SECRReportResultsTable
                      metric="emissions"
                      categories={result.categories}
                      totals={result.totals}
                      hasPriorYear={hasPriorYear}
                      prevYearLabel={prevYearLabel}
                      currentYearLabel={currentYearLabel}
                    />
                  </SECRReportSection>
                )}
              </>
            )}

            {sections.intensity && (
              <SECRReportSection
                title={sections.intensity.title}
                icon={SECTION_ICONS.intensity}
                onCopy={handleCopySection(sections.intensity!)}
              >
                <SECRReportIntensityTable
                  intensity={result.intensity}
                  prevYearLabel={prevYearLabel}
                  currentYearLabel={currentYearLabel}
                />
              </SECRReportSection>
            )}

            <SECRReportSection
              title={sections.methodology.title}
              icon={SECTION_ICONS.methodology}
              onCopy={handleCopySection(sections.methodology)}
            >
              <div className="bg-muted/20 text-foreground/80 space-y-3 rounded-lg border p-6 text-sm leading-relaxed">
                {methodologyParagraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </SECRReportSection>

            {sections.actions && result.config.actionsTaken && (
              <SECRReportSection
                title={sections.actions.title}
                icon={SECTION_ICONS.actions}
                onCopy={handleCopySection(sections.actions!)}
              >
                <div className="bg-muted/20 rounded-lg border p-6">
                  <div className="text-foreground/80 prose prose-sm max-w-none">
                    <ReactMarkdown>{result.config.actionsTaken}</ReactMarkdown>
                  </div>
                </div>
              </SECRReportSection>
            )}
          </div>
        </div>
      </main>
    </PageShell>
  );
}
